import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { EnvelopeSimple, Phone as PhoneIcon, Buildings, MapPin, ChatCircle } from "@phosphor-icons/react";

export default function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "Surveys2CAD | Leads";
        api.get("/admin/leads")
            .then((r) => setLeads(r.data.items))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-leads">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div className="min-w-0">
                    <div className="overline mb-2">From the website</div>
                    <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-50 tracking-tight">
                        Website Leads
                    </h1>
                    <p className="text-slate-400 mt-2 text-sm">
                        Contact form + Get Started + Quick Inquiry submissions from surveys2cad.com
                    </p>
                </div>
                <div className="font-mono-alt text-sm text-cyan-400" data-testid="leads-count">
                    {leads.length} total
                </div>
            </div>

            {(() => {
                if (loading) {
                    return <div className="tech-card p-10 text-slate-400 text-center">Loading…</div>;
                }
                if (leads.length === 0) {
                    return <div className="tech-card p-10 text-center text-slate-400">No leads yet.</div>;
                }
                return (
                <>
                    {/* --- Desktop table --- */}
                    <div className="tech-card hidden lg:block overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-800 bg-slate-900/40">
                                        {["Name", "Email", "Company", "State", "Service", "Source", "Message", "Received"].map((h) => (
                                            <th key={h} className="text-left px-4 py-3 overline">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map((l) => (
                                        <tr key={l.id} className="border-b border-slate-800/60 hover:bg-slate-900/30" data-testid={`lead-${l.id}`}>
                                            <td className="px-4 py-3 text-slate-100 font-heading font-medium">{l.name}</td>
                                            <td className="px-4 py-3 text-cyan-300 break-all">{l.email}</td>
                                            <td className="px-4 py-3 text-slate-300">{l.company || "—"}</td>
                                            <td className="px-4 py-3 text-slate-300">{l.state || "—"}</td>
                                            <td className="px-4 py-3 text-slate-300">{l.service_type || "—"}</td>
                                            <td className="px-4 py-3">
                                                <span className="px-2 py-0.5 rounded-full text-xs font-mono-alt bg-slate-800 text-slate-300">{l.source}</span>
                                            </td>
                                            <td className="px-4 py-3 text-slate-400 text-xs max-w-sm truncate">{l.message || "—"}</td>
                                            <td className="px-4 py-3 text-slate-400 font-mono-alt text-xs whitespace-nowrap">
                                                {l.created_at ? format(new Date(l.created_at), "MMM d, HH:mm") : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* --- Mobile / tablet card list --- */}
                    <div className="lg:hidden space-y-3" data-testid="leads-cards">
                        {leads.map((l) => (
                            <div key={l.id} className="tech-card p-4" data-testid={`lead-card-${l.id}`}>
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div className="min-w-0">
                                        <div className="font-heading font-semibold text-slate-50 text-base truncate">{l.name}</div>
                                        <div className="text-xs text-slate-500 font-mono-alt">
                                            {l.created_at ? format(new Date(l.created_at), "MMM d, HH:mm") : "—"}
                                        </div>
                                    </div>
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-alt bg-slate-800 text-slate-300 whitespace-nowrap">
                                        {l.source}
                                    </span>
                                </div>
                                <dl className="space-y-2 text-sm">
                                    <LeadRow icon={EnvelopeSimple} label={<a href={`mailto:${l.email}`} className="text-cyan-300 break-all">{l.email}</a>} />
                                    {l.phone && <LeadRow icon={PhoneIcon} label={<a href={`tel:${l.phone}`} className="text-slate-200">{l.phone}</a>} />}
                                    {l.company && <LeadRow icon={Buildings} label={<span className="text-slate-200">{l.company}</span>} />}
                                    {(l.state || l.service_type) && (
                                        <LeadRow
                                            icon={MapPin}
                                            label={
                                                <span className="text-slate-300">
                                                    {l.state || "—"}{l.service_type ? ` · ${l.service_type}` : ""}
                                                </span>
                                            }
                                        />
                                    )}
                                    {l.message && (
                                        <div className="flex items-start gap-2 pt-2 border-t border-slate-800/60">
                                            <ChatCircle size={14} weight="duotone" className="text-slate-500 mt-0.5 flex-shrink-0" />
                                            <p className="text-xs text-slate-400 whitespace-pre-wrap break-words">{l.message}</p>
                                        </div>
                                    )}
                                </dl>
                            </div>
                        ))}
                    </div>
                </>
                );
            })()}
        </div>
    );
}

function LeadRow({ icon: Icon, label }) {
    return (
        <div className="flex items-center gap-2 min-w-0">
            <Icon size={14} weight="duotone" className="text-slate-500 flex-shrink-0" />
            <div className="min-w-0 truncate">{label}</div>
        </div>
    );
}
